import React, { useState, useRef, useEffect } from 'react';

interface Panel {
  minWidth?: number;
  maxWidth?: number;
  defaultWidth?: number;
  collapsible?: boolean;
}

export interface ThreePanelHandle {
  toggleLeft: () => void;
  toggleRight: () => void;
}

interface ThreePanelLayoutProps {
  leftPanel?: {
    content: React.ReactNode;
    config?: Panel;
    initialCollapsed?: boolean;
  };
  centerPanel: {
    content: React.ReactNode;
    config?: Panel;
  };
  rightPanel?: {
    content: React.ReactNode;
    config?: Panel;
    initialCollapsed?: boolean;
  };
  className?: string;
  /** Ref for imperative panel toggle control (Cmd+B / Cmd+J) */
  controlRef?: React.MutableRefObject<ThreePanelHandle | null>;
}

/**
 * Three-panel layout with draggable splitters for the notebook interface.
 * Matches PropAlgos dark theme with smooth transitions.
 */
const STORAGE_KEY = 'propalgos-panel-widths';

const loadPersistedWidths = (): { left?: number; right?: number } => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const persistWidths = (left: number, right: number) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ left, right }));
  } catch { /* quota exceeded — ignore */ }
};

export const ThreePanelLayout: React.FC<ThreePanelLayoutProps> = ({
  leftPanel,
  centerPanel,
  rightPanel,
  className = '',
  controlRef,
}) => {
  const persisted = useRef(loadPersistedWidths());
  const [leftWidth, setLeftWidth] = useState(persisted.current.left || leftPanel?.config?.defaultWidth || 250);
  const [rightWidth, setRightWidth] = useState(persisted.current.right || rightPanel?.config?.defaultWidth || 400);
  const [leftCollapsed, setLeftCollapsed] = useState(leftPanel?.initialCollapsed || false);
  const [rightCollapsed, setRightCollapsed] = useState(rightPanel?.initialCollapsed || false);
  const [isDraggingLeft, setIsDraggingLeft] = useState(false);
  const [isDraggingRight, setIsDraggingRight] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // Expose toggle methods for keyboard shortcuts
  useEffect(() => {
    if (controlRef) {
      controlRef.current = {
        toggleLeft: () => setLeftCollapsed(prev => !prev),
        toggleRight: () => setRightCollapsed(prev => !prev),
      };
    }
  }, [controlRef]);

  // Persist widths to localStorage on drag end
  useEffect(() => {
    if (!isDraggingLeft && !isDraggingRight) {
      persistWidths(leftWidth, rightWidth);
    }
  }, [isDraggingLeft, isDraggingRight, leftWidth, rightWidth]);

  // handle left splitter drag
  useEffect(() => {
    if (!isDraggingLeft) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = e.clientX - containerRect.left;
      const minWidth = leftPanel?.config?.minWidth || 200;
      const maxWidth = leftPanel?.config?.maxWidth || 500;
      
      setLeftWidth(Math.min(Math.max(newWidth, minWidth), maxWidth));
    };

    const handleMouseUp = () => {
      setIsDraggingLeft(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingLeft, leftPanel?.config]);

  // handle right splitter drag
  useEffect(() => {
    if (!isDraggingRight) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = containerRect.right - e.clientX;
      const minWidth = rightPanel?.config?.minWidth || 300;
      const maxWidth = rightPanel?.config?.maxWidth || 600;
      
      setRightWidth(Math.min(Math.max(newWidth, minWidth), maxWidth));
    };

    const handleMouseUp = () => {
      setIsDraggingRight(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingRight, rightPanel?.config]);

  return (
    <div 
      ref={containerRef}
      className={`flex h-full bg-black ${className} ${
        (isDraggingLeft || isDraggingRight) ? 'select-none' : ''
      }`}
    >
      {/* left panel */}
      {leftPanel && (
        <>
          <div 
            className={`relative transition-all duration-300 bg-gray-900 border-r border-gray-800 ${
              leftCollapsed ? 'cursor-pointer hover:bg-gray-850' : 'overflow-hidden'
            }`}
            style={{ width: leftCollapsed ? 48 : leftWidth }}
          >
            {leftCollapsed ? (
              /* Collapsed state - narrow vertical bar with expand button */
              <button
                onClick={() => setLeftCollapsed(false)}
                className="w-full h-full flex items-center justify-center text-gray-500 hover:text-white"
                title="Expand file explorer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </button>
            ) : (
              <>
                {/* collapse button - positioned on the right edge of the panel */}
                {leftPanel.config?.collapsible && (
                  <button
                    onClick={() => setLeftCollapsed(!leftCollapsed)}
                    className="absolute top-2 right-2 z-20 p-1 bg-gray-800 rounded text-gray-400 hover:text-white hover:bg-gray-700"
                    title="Collapse panel"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="h-full overflow-auto">
                  {leftPanel.content}
                </div>
              </>
            )}
          </div>
          
          {/* left splitter */}
          <div
            className={`w-1 bg-gray-800 hover:bg-blue-600 cursor-col-resize transition-colors ${
              leftCollapsed ? 'hidden' : ''
            }`}
            onMouseDown={() => setIsDraggingLeft(true)}
            onDoubleClick={() => {
              const defaultWidth = leftPanel?.config?.defaultWidth || 250;
              setLeftWidth(defaultWidth);
            }}
          />
        </>
      )}

      {/* center panel */}
      <div className="flex-1 bg-gray-950 overflow-hidden">
        <div className="h-full overflow-auto">
          {centerPanel.content}
        </div>
      </div>

      {/* right panel */}
      {rightPanel && (
        <>
          {/* right splitter */}
          <div
            className={`w-1 bg-gray-800 hover:bg-blue-600 cursor-col-resize transition-colors ${
              rightCollapsed ? 'hidden' : ''
            }`}
            onMouseDown={() => setIsDraggingRight(true)}
            onDoubleClick={() => {
              const defaultWidth = rightPanel?.config?.defaultWidth || 400;
              setRightWidth(defaultWidth);
            }}
          />
          
          <div
            className={`relative transition-all duration-300 bg-gray-900 border-l border-gray-800 ${
              rightCollapsed ? 'cursor-pointer hover:bg-gray-850' : 'overflow-hidden'
            }`}
            style={{ width: rightCollapsed ? 48 : rightWidth }}
          >
            {rightCollapsed ? (
              <button
                onClick={() => setRightCollapsed(false)}
                className="w-full h-full flex items-center justify-center text-gray-500 hover:text-white"
                title="Expand panel"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
              </button>
            ) : (
              <>
                {rightPanel.config?.collapsible && (
                  <button
                    onClick={() => setRightCollapsed(true)}
                    className="absolute top-2 right-2 z-10 p-1 bg-gray-800 rounded text-gray-400 hover:text-white hover:bg-gray-700"
                    title="Collapse panel"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
                <div className="h-full overflow-auto">
                  {rightPanel.content}
                </div>
              </>
            )}
          </div>
        </>
      )}

    </div>
  );
};